import { useQuery } from '@tanstack/vue-query';
import { useStore } from '@/store/useStore';
import { request } from '@/api/request';
import type { Tag } from '@@/packages/shared/types/models/tag';
import { mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import { TAGS_QUERY } from './queryKeys';

export function useTags(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [TAGS_QUERY],
      async queryFn() {
        const res = await request<(Tag | undefined)[]>('GET', '/tags');
        return res.data;
      },
    }),
  );

  const store = useStore();
  watch(query.isError, (isError) => {
    if (isError && store) void store.dispatch('legacy/setGlobalError', 'API_ERROR_TAGS');
  });

  const tags = computed(() => query.data.value?.filter((tag) => !!tag) || []);

  const filterTags = (type: string) => tags.value.filter((tag) => tag.type === type);

  function getTag(tagID: string) {
    return tags.value.find((tag) => tag.tagID === tagID);
  }

  return {
    ...query,
    query,
    tags,
    criticalityTags: computed(() => filterTags('criticality')),
    criticalityTagsByName: computed(() =>
      Object.fromEntries(filterTags('criticality').map((tag) => [tag.tag, tag])),
    ),
    confidentialityTags: computed(() => filterTags('confidentiality')),
    piiTags: computed(() => filterTags('pii')),
    industryTags: computed(() => filterTags('industry')),
    tagsByID: computed(() => Object.fromEntries(tags.value.map((tag) => [tag.tagID, tag]))),
    getTag,
  };
}
